import type { CustomerSlaConfigurationPayload } from "@/utils/types/slaConfiguration";

export type SlaSchemaFormValues = {
  sla_policy_schema?: string;
  business_hours_schema?: string;
  holiday_calendar_schema?: string;
  escalation_time_schema?: string;
  notify_role_foreign_key_schema?: string;
};

const FORM_TO_API_SCHEMA: ReadonlyArray<{
  field: keyof SlaSchemaFormValues;
  schema_type: string;
}> = [
  { field: "sla_policy_schema", schema_type: "sla_policy" },
  { field: "business_hours_schema", schema_type: "business_hours" },
  { field: "holiday_calendar_schema", schema_type: "holiday_calendar" },
  { field: "escalation_time_schema", schema_type: "escalation_time" },
  { field: "notify_role_foreign_key_schema", schema_type: "applye_table" },
];

export function buildCustomerSlaConfigurationPayload(
  values: SlaSchemaFormValues
): CustomerSlaConfigurationPayload {
  return {
    data_source_type: "FORMILY",
    schemas: FORM_TO_API_SCHEMA.map(({ field, schema_type }) => ({
      schema_type,
      table_name: String(values[field] ?? "").trim(),
    })),
  };
}

/** API record → form field values for edit. */
export function parseCustomerSlaConfigurationToForm(
  record: unknown
): SlaSchemaFormValues & { id?: string } {
  if (!record || typeof record !== "object") {
    return {};
  }
  const r = record as Record<string, unknown>;
  const schemas = Array.isArray(r.schemas) ? r.schemas : [];
  const values: SlaSchemaFormValues = {};

  for (const item of schemas) {
    if (!item || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    const schemaType = String(row.schema_type ?? "").trim();
    const tableName = String(row.table_name ?? "").trim();
    const mapping = FORM_TO_API_SCHEMA.find(m => m.schema_type === schemaType);
    if (mapping && tableName) {
      values[mapping.field] = tableName;
    }
  }

  const id = r.id ?? r.pk ?? r.uuid;
  return {
    ...values,
    id: id != null ? String(id) : undefined,
  };
}

export function normalizeCustomerSlaConfigurationList(
  data: unknown
): Record<string, unknown>[] {
  if (Array.isArray(data)) {
    return data.filter((x): x is Record<string, unknown> => !!x && typeof x === "object");
  }
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    if (Array.isArray(o.results)) {
      return normalizeCustomerSlaConfigurationList(o.results);
    }
    if (Array.isArray(o.data)) {
      return normalizeCustomerSlaConfigurationList(o.data);
    }
  }
  return [];
}

export function customerSlaRecordToTableRow(
  record: Record<string, unknown>
): SlaSchemaFormValues & { id: string } {
  const parsed = parseCustomerSlaConfigurationToForm(record);
  const id = parsed.id ?? String(record.id ?? record.pk ?? "");
  return { id, ...parsed };
}
